import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { StylovyzeCard, useUnits } from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations/useGlobalization';
import { GRADE_CONFIG } from '@Components/RiskDetails/Datagrid/gradeConfig';
import { GradeKey, RiskResultsSummary } from '@Types/riskResultsSummary.types';
import {
	buildGradeCards,
	formatResultsLength,
	formatShare,
	gradeLabel,
} from './resultsSummary.utils';
import { KpiBreakdownPanel } from './KpiBreakdownPanel.component';
import { RESULTS_LINK_COLOR } from '@Components/RiskDetails/linkColor';

interface Props {
	summary: RiskResultsSummary;
	assetType?: string;
}

// Per-grade KPI cards (Venus pattern): the whole card toggles a breakdown
// panel below the row. The "See breakdown" link is revealed on hover and
// keyboard focus and stays while selected. Deliberately does not filter the
// grid — that belongs to the Results grid slice (I360A-2743).
export const ResultsKpiBar = ({ summary, assetType }: Props) => {
	const { t } = useGlobalization();
	const { system, getSystemValueFormatted } = useUnits();
	const [selected, setSelected] = useState<GradeKey | null>(null);

	const cards = buildGradeCards(summary);
	const toggle = (grade: GradeKey) =>
		setSelected(current => (current === grade ? null : grade));

	return (
		<Box sx={{ mb: 2 }} data-cy="risk-results-kpi">
			<Box
				sx={{
					display: 'flex',
					gap: 1.5,
					'& .MuiCard-root': {
						boxShadow: 'none',
						border: '1px solid #e5e7eb',
					},
				}}>
				{cards.map(card => {
					const cfg = GRADE_CONFIG[card.grade];
					const isSelected = selected === card.grade;
					return (
						<Box
							key={card.grade}
							role="button"
							tabIndex={0}
							aria-expanded={isSelected}
							data-testid={`risk-grade-card-${card.grade}`}
							data-cy={`risk-grade-card-${card.grade}`}
							onClick={() => toggle(card.grade)}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									toggle(card.grade);
								}
							}}
							sx={{
								flex: 1,
								cursor: 'pointer',
								position: 'relative',
								borderRadius: 1,
								border: `2px solid ${
									isSelected ? cfg.color : 'transparent'
								}`,
								transition: 'border-color 0.2s ease-in-out',
								outline: 'none',
								'&:hover .see-breakdown, &:focus-visible .see-breakdown':
									{ opacity: 1 },
								'&:focus-visible': {
									boxShadow: `0 0 0 2px ${cfg.color}`,
								},
							}}>
							<StylovyzeCard
								topColor={cfg.color}
								countText={card.count}
								titlePosition="above"
								topSubText={t('Assets')}
								topText={gradeLabel(t, card.grade)}
								bottomTextLeft={
									<Typography variant="body2">
										{`${t(
											'Total length',
										)}: ${formatResultsLength(
											card.lengthMetres,
											system,
											getSystemValueFormatted,
										)}`}
									</Typography>
								}
								bottomTextRight={
									<Typography variant="body2">
										{`${formatShare(card.share)} ${t(
											'of scored assets',
										)}`}
									</Typography>
								}
							/>
							<Box
								className="see-breakdown"
								data-testid={`risk-grade-breakdown-toggle-${card.grade}`}
								data-cy={`risk-grade-breakdown-toggle-${card.grade}`}
								sx={{
									opacity: isSelected ? 1 : 0,
									transition: 'opacity 0.15s ease',
									position: 'absolute',
									bottom: 0,
									left: 0,
									right: 0,
									textAlign: 'center',
									fontSize: '0.75rem',
									color: RESULTS_LINK_COLOR,
									py: '8px',
									backgroundColor: '#ffffff',
									borderTop: '1px solid #f3f4f6',
									borderRadius: '0 0 4px 4px',
									'&:hover': { textDecoration: 'underline' },
								}}>
								{isSelected
									? t('Hide breakdown ↑')
									: t('See breakdown ↓')}
							</Box>
						</Box>
					);
				})}
			</Box>
			{summary.unscored > 0 && (
				<Typography
					variant="caption"
					data-testid="risk-results-unscored"
					data-cy="risk-results-unscored"
					sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
					{t('{{count}} assets not scored', {
						count: summary.unscored,
					})}
				</Typography>
			)}
			{selected && (
				<Box sx={{ mt: 2 }}>
					<KpiBreakdownPanel
						grade={selected}
						breakdown={summary.breakdown[selected]}
						assetType={assetType}
						onClose={() => setSelected(null)}
					/>
				</Box>
			)}
		</Box>
	);
};

export default ResultsKpiBar;
